import { Action, createReducer, on } from '@ngrx/store';
import {
  FareCalculator,
  IGeoCoords,
  OffPlatformReservationInfo,
  Person,
  ProviderDetail,
  ProviderFeeSchedule,
  ProviderFeeScheduleApprovalObjectInfo,
  ProviderFeeScheduleApprovalStatus,
  ProviderServiceLevel,
  ServiceLevel,
  Tenant,
  TransitProgram,
  User,
  ProviderDispatchFeedItem,
  ProviderDriver,
  Provider,
  ProviderDispatcher
} from '@uzurv/clara';
import { ApprovalObjectStatusCodeConstants, AutoCompleteResult, isNullOrUndefined } from '@uzurv/core';
import { Serialize } from 'cerialize';
import { cloneDeep, get, orderBy } from 'lodash';
import { ApprovalRequestStatus } from '../../../../sdk/packages/clara/src/models/approval-request-status';

import * as dispatchActions from './dispatch.actions';

export class ProviderDetailState {
  provider?: ProviderDetail;
  providerTransitPrograms?: Array<TransitProgram>;
  providerFeeSchedules?: Array<ProviderFeeScheduleApprovalStatus>;
  providerServiceLevels?: Array<ProviderServiceLevel>;
  dispatchers?: Array<ProviderDispatcher>;
  drivers?: Array<ProviderDriver>;
  selectedTabIndex?: number;
  editing?: boolean;

  selectedFeeScheduleStatus?: string;
  shouldDisplayProviderFeeScheduleApprovalStatusList?: boolean;
  selectedTransitProgramId?: number;
  selectedProviderServiceLevel?: ProviderServiceLevel;
  openPanelIndex?: number;
  providerFeeScheduleApprovalStatusesForProviderServiceLevel?: Array<ProviderFeeScheduleApprovalStatus>;
  selectedProviderFeeScheduleApprovalObjectInfo?: ProviderFeeScheduleApprovalObjectInfo;
  providerFeeScheduleFormData?: {
    providerFeeScheduleEditableValues: ProviderFeeSchedule;
    providerFeeScheduleNonEditableValues: ProviderFeeSchedule;
  };
  // If a fare calculator has changed from the Current version, a new layout is applied where all values display as modified.
  hasModifiedFareCalculator?: boolean;
}

export class DispatchFilters {
  upperLatitude?: number;
  leftLongitude?: number;
  lowerLatitude?: number;
  rightLongitude?: number;
  beginDate?: Date;
  endDate?: Date;
  beginTime?: string;
  endTime?: string;
}

export const dispatchFeatureKey = 'dispatch';

export interface DispatchState {
  loading: boolean;
  providerId: number;
  pageNumber: number;
  pageSize: number;
  ridesTab: 'Ride Requests' | 'Scheduled';
  includeIgnored: boolean;
  mapInitialized: boolean;
  mapCenter: IGeoCoords;
  filters: DispatchFilters;
  deferHoverEvents: boolean;
  providerSearchString: string;
  providers: Array<Provider>;
  providerDetails: { [id: number]: ProviderDetailState };
  serviceLevels: Array<ServiceLevel>;
  fareCalculators: Array<FareCalculator>;
  dispatchers: Array<ProviderDispatcher>;
  tenants: Array<Tenant>;
  drivers: Array<ProviderDriver>;
  rides: Array<ProviderDispatchFeedItem>;
  pinnedRides: Array<ProviderDispatchFeedItem>;
  autocompleteResults: Array<AutoCompleteResult>;
  isItineraryFocused: boolean;
  focusedItineraryRides: Array<ProviderDispatchFeedItem>;
  focusedItineraryRiderName: string;
  onDemandRides: Array<any>;
  offPlatformReservations: Array<OffPlatformReservationInfo>;
  usersByClientApproverRole: Array<User>;
  usersByProviderApproverRole: Array<User>;
  approvalRequestStatuses: Array<ApprovalRequestStatus>;
}

export const initialState: DispatchState = {
  loading: false,
  providerId: null,
  pageNumber: 0,
  pageSize: 20,
  ridesTab: 'Ride Requests',
  includeIgnored: false,
  mapInitialized: false,
  mapCenter: null,
  filters: new DispatchFilters(),
  deferHoverEvents: false,
  providerSearchString: '',
  providers: [],
  providerDetails: {},
  serviceLevels: [],
  fareCalculators: [],
  dispatchers: [],
  tenants: [],
  drivers: [],
  rides: [],
  pinnedRides: [],
  autocompleteResults: [],
  isItineraryFocused: false,
  focusedItineraryRides: [],
  focusedItineraryRiderName: null,
  onDemandRides: [],
  offPlatformReservations: null,
  usersByClientApproverRole: [],
  usersByProviderApproverRole: [],
  approvalRequestStatuses: []
};

const dispatchReducer = createReducer(
  initialState,
  on(dispatchActions.restoreState, (_, payload) => ({ ...payload.state })),

  on(dispatchActions.setProviderId, (state, payload) => ({ ...state, providerId: payload.id })),

  on(dispatchActions.resetPagination, state => ({ ...state, pageNumber: 0, pageSize: 20 })),

  on(dispatchActions.getProvider, state => ({ ...state, loading: true })),

  on(dispatchActions.getProviderFail, state => ({ ...state, loading: false })),

  on(dispatchActions.getProviderSuccess, (state, payload) => ({
    ...state,
    loading: false,
    providerDetails: {
      ...state.providerDetails,
      [payload.provider.id]: {
        provider: payload.provider
      }
    }
  })),

  on(dispatchActions.listProvidersSuccess, (state, payload) => {
    // we currently don't support multiple provider views for rides
    // so for operators this will always be the only provider
    // otherwise, a UZURV role is required to view multiple providers
    // the null check handles having no operator records
    if (isNullOrUndefined(payload.providers)) {
      return state;
    } else if (payload.providers?.length === 1) {
      return {
        ...state,
        providers: payload.providers,
        loading: false,
        providerId: payload.providers[0].id
      };
    } else {
      return {
        ...state,
        providers: payload.providers,
        providerDetails: payload.providers.reduce((dict, provider) => {
          const providerDetail: ProviderDetail = provider as ProviderDetail;
          if (isNullOrUndefined(dict[provider.id])) {
            dict[provider.id] = { provider: providerDetail };
          } else {
            dict[provider.id].provider = providerDetail;
          }
          return dict;
        }, state.providerDetails),
        loading: false
      };
    }
  }),
  on(dispatchActions.listProvidersFail, state => ({ ...state, loading: false })),

  on(dispatchActions.searchProviders, (state, payload) => ({
    ...state,
    providerSearchString: payload.searchString
  })),

  on(dispatchActions.beginEditProvider, (state, payload) => ({
    ...state,
    providerDetails: {
      ...state.providerDetails,
      [payload.providerId]: {
        ...state.providerDetails[payload.providerId],
        editing: true
      }
    }
  })),

  on(
    dispatchActions.cancelEditProvider,
    dispatchActions.editProviderSuccess,
    dispatchActions.saveProviderFeeScheduleSuccess,
    dispatchActions.resubmitProviderFeeScheduleSuccess,
    (state, payload) => ({
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerId]: {
          ...state.providerDetails[payload.providerId],
          editing: false
        }
      }
    })
  ),

  on(
    dispatchActions.saveProviderFeeScheduleSuccess,
    dispatchActions.resubmitProviderFeeScheduleSuccess,
    dispatchActions.deleteProviderFeeScheduleSuccess,
    dispatchActions.updateApprovalSuccess,
    (state, payload) => {
      return {
        ...state,
        providerDetails: {
          ...state.providerDetails,
          [payload.providerId]: {
            ...state.providerDetails[payload.providerId],
            shouldDisplayProviderFeeScheduleApprovalStatusList: true
          }
        }
      };
    }
  ),

  on(dispatchActions.listProviderFeeScheduleApprovalStatusesSuccess, (state, payload) => {
    // If the selectedProviderServiceLevel is set, then reload the providerFeeScheduleApprovalStatusesForProviderServiceLevel
    let providerFeeScheduleApprovalStatusesForServiceLevel: Array<ProviderFeeScheduleApprovalStatus> = [];
    const selectedProviderServiceLevel: ProviderServiceLevel = state.providerDetails[state.providerId].selectedProviderServiceLevel;
    if (!isNullOrUndefined(selectedProviderServiceLevel)) {
      providerFeeScheduleApprovalStatusesForServiceLevel = payload.providerFeeScheduleApprovalStatuses.filter(
        providerFeeScheduleApprovalStatus => {
          return (
            providerFeeScheduleApprovalStatus.providerFeeSchedule.transitProgramId ===
              state.providerDetails[state.providerId].selectedTransitProgramId &&
            providerFeeScheduleApprovalStatus.providerFeeSchedule.providerId === selectedProviderServiceLevel.providerId &&
            providerFeeScheduleApprovalStatus.providerFeeSchedule.serviceLevelId === selectedProviderServiceLevel.serviceLevelId
          );
        }
      );

      const doesCurrentExist: boolean =
        providerFeeScheduleApprovalStatusesForServiceLevel.findIndex(approvalStatus => {
          return approvalStatus.status === ApprovalObjectStatusCodeConstants.CURRENT;
        }) !== -1;

      if (!doesCurrentExist) {
        const defaultProviderFeeSchedule: ProviderFeeScheduleApprovalStatus =
          ProviderFeeScheduleHelper.buildDefaultProviderFeeScheduleApprovalStatus(
            selectedProviderServiceLevel.providerId,
            state.providerDetails[state.providerId].selectedTransitProgramId,
            selectedProviderServiceLevel.serviceLevelId
          );
        providerFeeScheduleApprovalStatusesForServiceLevel.unshift(defaultProviderFeeSchedule);
      }
    }

    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerId]: {
          ...state.providerDetails[payload.providerId],
          providerFeeSchedules: payload.providerFeeScheduleApprovalStatuses,
          providerFeeScheduleApprovalStatusesForProviderServiceLevel: providerFeeScheduleApprovalStatusesForServiceLevel
        }
      }
    };
  }),

  on(dispatchActions.clearProviderFeeScheduleFormData, (state, payload) => {
    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerId]: {
          ...state.providerDetails[payload.providerId],
          providerFeeScheduleFormData: {
            providerFeeScheduleEditableValues: null,
            providerFeeScheduleNonEditableValues: null
          }
        }
      }
    };
  }),

  on(dispatchActions.clearProviderDetail, (state, payload) => {
    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerId]: null
      }
    };
  }),

  on(dispatchActions.listProviderServiceLevelsSuccess, (state, payload) => ({
    ...state,
    provider: {
      ...state.providerDetails,
      [payload.providerId]: {
        ...state.providerDetails[payload.providerId],
        providerServiceLevels: payload.serviceLevels
      }
    }
  })),

  on(dispatchActions.getProviderSuccess, (state, payload) => {
    // If the selectedProviderServiceLevel is set, then reload the providerFeeScheduleApprovalStatusesForProviderServiceLevel
    let providerFeeScheduleApprovalStatusesForServiceLevel: Array<ProviderFeeScheduleApprovalStatus> = [];
    const selectedProviderServiceLevel: ProviderServiceLevel = state.providerDetails[state.providerId].selectedProviderServiceLevel;
    if (!isNullOrUndefined(selectedProviderServiceLevel)) {
      providerFeeScheduleApprovalStatusesForServiceLevel = payload.provider.providerFeeSchedules.filter(
        providerFeeScheduleApprovalStatus => {
          return (
            providerFeeScheduleApprovalStatus.providerFeeSchedule.transitProgramId ===
              state.providerDetails[state.providerId].selectedTransitProgramId &&
            providerFeeScheduleApprovalStatus.providerFeeSchedule.providerId === selectedProviderServiceLevel.providerId &&
            providerFeeScheduleApprovalStatus.providerFeeSchedule.serviceLevelId === selectedProviderServiceLevel.serviceLevelId
          );
        }
      );
    }

    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.provider.id]: {
          ...state.providerDetails[payload.provider.id],
          provider: payload.provider,
          providerTransitPrograms: payload.provider.transitPrograms,
          providerFeeSchedules: payload.provider.providerFeeSchedules,
          providerServiceLevels: payload.provider.providerServiceLevels,
          providerFeeScheduleApprovalStatusesForProviderServiceLevel: providerFeeScheduleApprovalStatusesForServiceLevel
        }
      }
    };
  }),

  on(dispatchActions.listUsersByClientApproverRoleSuccess, (state, payload) => ({
    ...state,
    usersByClientApproverRole: payload.usersByClientApproverRole
  })),

  on(dispatchActions.listUsersByProviderApproverRoleSuccess, (state, payload) => ({
    ...state,
    usersByProviderApproverRole: payload.usersByProviderApproverRole
  })),

  on(dispatchActions.listApprovalRequestStatusesSuccess, (state, payload) => ({
    ...state,
    approvalRequestStatuses: payload.approvalRequestStatuses
  })),

  on(dispatchActions.selectProviderServiceLevel, (state, payload) => {
    // The current provider fee schedule is always selected when expanding a panel (unless it is new).

    let currentProviderFeeSchedule: ProviderFeeSchedule = ProviderFeeScheduleHelper.getCurrentProviderFeeScheduleApprovalStatus(
      state.providerDetails[payload.providerServiceLevel.providerId].providerFeeSchedules,
      payload.providerServiceLevel.providerId,
      payload.transitProgramId,
      payload.providerServiceLevel.serviceLevelId
    )?.providerFeeSchedule;

    // TODO: I don't think a current will ever NOT exist.
    // Create a new Current (default) fee schedule if one doesn't exist.
    if (isNullOrUndefined(currentProviderFeeSchedule)) {
      currentProviderFeeSchedule = ProviderFeeScheduleHelper.buildNewProviderFeeSchedule(
        payload.providerServiceLevel.providerId,
        payload.transitProgramId,
        payload.providerServiceLevel.serviceLevelId
      );
    }

    const providerFeeScheduleFormData: {
      providerFeeScheduleEditableValues: ProviderFeeSchedule;
      providerFeeScheduleNonEditableValues: ProviderFeeSchedule;
    } = ProviderFeeScheduleHelper.getProviderFeeScheduleFormData(currentProviderFeeSchedule);

    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerServiceLevel.providerId]: {
          ...state.providerDetails[payload.providerServiceLevel.providerId],
          selectedProviderServiceLevel: payload.providerServiceLevel,
          selectedTransitProgramId: payload.transitProgramId,
          selectedFeeScheduleStatus: ApprovalObjectStatusCodeConstants.CURRENT,
          providerFeeScheduleFormData: providerFeeScheduleFormData
        }
      }
    };
  }),

  on(dispatchActions.handleProviderFeeScheduleApprovalStatusSelected, (state, payload) => {
    const approvalObjectStatus: any = state.providerDetails[
      state.providerId
    ].providerFeeScheduleApprovalStatusesForProviderServiceLevel.find(pfs => {
      return pfs.providerFeeSchedule.id == payload.providerFeeScheduleId;
    });

    // A fee schedule approval status has been selected from the list. Create new providerFeeScheduleFormData based on the current and selected.
    const selectedProviderFeeSchedule: ProviderFeeSchedule = approvalObjectStatus.providerFeeSchedule;

    let currentProviderFeeSchedule: ProviderFeeSchedule = ProviderFeeScheduleHelper.getCurrentProviderFeeScheduleApprovalStatus(
      state.providerDetails[selectedProviderFeeSchedule.providerId].provider.providerFeeSchedules,
      selectedProviderFeeSchedule.providerId,
      selectedProviderFeeSchedule.transitProgramId,
      selectedProviderFeeSchedule.serviceLevelId
    )?.providerFeeSchedule;

    if (isNullOrUndefined(currentProviderFeeSchedule)) {
      currentProviderFeeSchedule = ProviderFeeScheduleHelper.buildNewProviderFeeSchedule(
        selectedProviderFeeSchedule.providerId,
        selectedProviderFeeSchedule.transitProgramId,
        selectedProviderFeeSchedule.serviceLevelId
      );
    }

    let hasModifiedFareCalculator: boolean = false;
    if (currentProviderFeeSchedule.fareCalculatorId !== selectedProviderFeeSchedule.fareCalculatorId) {
      hasModifiedFareCalculator = true;
    }

    const providerFeeScheduleFormData: {
      providerFeeScheduleEditableValues: ProviderFeeSchedule;
      providerFeeScheduleNonEditableValues: ProviderFeeSchedule;
    } = ProviderFeeScheduleHelper.getProviderFeeScheduleFormData(
      currentProviderFeeSchedule,
      selectedProviderFeeSchedule,
      hasModifiedFareCalculator
    );

    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [selectedProviderFeeSchedule.providerId]: {
          ...state.providerDetails[selectedProviderFeeSchedule.providerId],
          selectedFeeScheduleStatus: approvalObjectStatus.status,
          providerFeeScheduleFormData: providerFeeScheduleFormData,
          shouldDisplayProviderFeeScheduleApprovalStatusList: false,
          hasModifiedFareCalculator: hasModifiedFareCalculator,
          selectedProviderFeeScheduleApprovalObjectInfo: null
        }
      }
    };
  }),

  on(dispatchActions.selectTransitProgramForProvider, (state, payload) => {
    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerId]: {
          ...state.providerDetails[payload.providerId],
          selectedTransitProgramId: payload.transitProgramId,
          shouldDisplayProviderFeeScheduleApprovalStatusList: false
        }
      }
    };
  }),

  on(dispatchActions.setOpenPanelIndex, (state, payload) => {
    const providerServiceLevelIndex = state.providerDetails[payload.providerId].provider.providerServiceLevels.findIndex(
      providerServiceLevel => {
        return providerServiceLevel.serviceLevelId == payload.providerServiceLevel.serviceLevelId;
      }
    );

    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerId]: {
          ...state.providerDetails[payload.providerId],
          openPanelIndex: providerServiceLevelIndex,
          selectedProviderFeeScheduleApprovalObjectInfo: null
        }
      }
    };
  }),

  on(dispatchActions.selectProviderFeeScheduleApprovalStatusesForProviderServiceLevel, (state, payload) => {
    const clonedProviderFeeScheduleApprovalStatuses: Array<ProviderFeeScheduleApprovalStatus> =
      state.providerDetails[payload.providerServiceLevel.providerId].providerFeeSchedules;

    const providerFeeScheduleApprovalStatusesForServiceLevel: Array<ProviderFeeScheduleApprovalStatus> =
      clonedProviderFeeScheduleApprovalStatuses.filter(providerFeeScheduleApprovalStatus => {
        return (
          providerFeeScheduleApprovalStatus.providerFeeSchedule.providerId == payload.providerServiceLevel.providerId &&
          providerFeeScheduleApprovalStatus.providerFeeSchedule.transitProgramId == payload.transitProgramId &&
          providerFeeScheduleApprovalStatus.providerFeeSchedule.serviceLevelId == payload.providerServiceLevel.serviceLevelId
        );
      });

    const doesCurrentExist: boolean =
      providerFeeScheduleApprovalStatusesForServiceLevel.findIndex(approvalStatus => {
        return approvalStatus.status === ApprovalObjectStatusCodeConstants.CURRENT;
      }) !== -1;

    if (!doesCurrentExist) {
      const providerFeeScheduleApprovalStatus: ProviderFeeScheduleApprovalStatus =
        ProviderFeeScheduleHelper.buildDefaultProviderFeeScheduleApprovalStatus(
          payload.providerServiceLevel.providerId,
          payload.transitProgramId,
          payload.providerServiceLevel.serviceLevelId
        );
      providerFeeScheduleApprovalStatusesForServiceLevel.unshift(providerFeeScheduleApprovalStatus);
    }

    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerServiceLevel.providerId]: {
          ...state.providerDetails[payload.providerServiceLevel.providerId],
          providerFeeScheduleApprovalStatusesForProviderServiceLevel: providerFeeScheduleApprovalStatusesForServiceLevel,
          shouldDisplayProviderFeeScheduleApprovalStatusList: true,
          selectedProviderFeeScheduleApprovalObjectInfo: null,
          selectedProviderServiceLevel: payload.providerServiceLevel
        }
      }
    };
  }),

  on(dispatchActions.findProviderFeeScheduleInfoByIdSuccess, (state, payload) => {
    return {
      ...state,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerFeeScheduleApprovalObjectInfo.providerFeeSchedule.providerId]: {
          ...state.providerDetails[payload.providerFeeScheduleApprovalObjectInfo.providerFeeSchedule.providerId],
          selectedProviderFeeScheduleApprovalObjectInfo: payload.providerFeeScheduleApprovalObjectInfo
        }
      }
    };
  }),

  on(dispatchActions.addTransitProgram, (state, payload) => {
    const clonedProviderFeeScheduleApprovalStatusesForServiceLevel: Array<ProviderFeeScheduleApprovalStatus> = cloneDeep(
      state.providerDetails[payload.providerId].providerFeeScheduleApprovalStatusesForProviderServiceLevel
    );

    state.providerDetails[payload.providerId].providerServiceLevels.forEach(providerServiceLevel => {
      const defaultProviderFeeScheduleApprovalStatus = ProviderFeeScheduleHelper.buildDefaultProviderFeeScheduleApprovalStatus(
        payload.providerId,
        payload.transitProgram.id,
        providerServiceLevel.serviceLevelId
      );
      clonedProviderFeeScheduleApprovalStatusesForServiceLevel.push(defaultProviderFeeScheduleApprovalStatus);
    });

    const clonedTransitPrograms: Array<TransitProgram> = cloneDeep(state.providerDetails[payload.providerId].providerTransitPrograms);
    clonedTransitPrograms.push(payload.transitProgram);

    return {
      ...state,
      transitPrograms: clonedTransitPrograms,
      providerDetails: {
        ...state.providerDetails,
        [payload.providerId]: {
          ...state.providerDetails[payload.providerId],
          providerTransitPrograms: clonedTransitPrograms,
          providerFeeScheduleApprovalStatusesForProviderServiceLevel: clonedProviderFeeScheduleApprovalStatusesForServiceLevel,
          selectedTransitProgramId: payload.transitProgram.id,
          shouldDisplayProviderFeeScheduleApprovalStatusList: false
        }
      }
    };
  }),

  on(dispatchActions.selectProviderTab, (state, payload) => ({
    ...state,
    providerDetails: {
      ...state.providerDetails,
      [payload.providerId]: {
        ...state.providerDetails[payload.providerId],
        selectedTabIndex: payload.tabIndex
      }
    }
  })),

  on(dispatchActions.listServiceLevelsSuccess, (state, payload) => ({
    ...state,
    serviceLevels: payload.serviceLevels
  })),

  on(dispatchActions.listFareCalculatorsSuccess, (state, payload) => ({
    ...state,
    fareCalculators: payload.fareCalculators
  })),

  on(dispatchActions.listDispatchersSuccess, (state, payload) => ({ ...state, dispatchers: payload.dispatchers, loading: false })),
  on(dispatchActions.listDispatchersFail, state => ({ ...state, loading: false })),

  on(dispatchActions.listTenantsSuccess, (state, payload) => ({ ...state, tenants: payload.tenants, loading: false })),
  on(dispatchActions.listTenantsFail, state => ({ ...state, loading: false })),

  on(dispatchActions.createDispatcher, state => ({ ...state, loading: true })),
  on(dispatchActions.createDispatcherSuccess, state => ({ ...state, loading: false })),
  on(dispatchActions.createDispatcherFail, state => ({ ...state, loading: false })),

  on(dispatchActions.deleteDispatcher, state => ({ ...state, loading: true })),
  on(dispatchActions.deleteDispatcherSuccess, state => ({ ...state, loading: false })),
  on(dispatchActions.deleteDispatcherFail, state => ({ ...state, loading: false })),

  on(dispatchActions.listDriversSuccess, (state, payload) => ({ ...state, drivers: payload.drivers, loading: false })),
  on(dispatchActions.listDriversFail, state => ({ ...state, loading: false })),

  on(dispatchActions.pageProvidersList, (state, payload) => ({ ...state, pageNumber: payload.pageNumber, pageSize: payload.pageSize })),
  on(dispatchActions.pageDriversList, (state, payload) => ({ ...state, pageNumber: payload.pageNumber, pageSize: payload.pageSize })),
  on(dispatchActions.pageDispatchersList, (state, payload) => ({
    ...state,
    pageNumber: payload.pageNumber,
    pageSize: payload.pageSize
  })),

  on(dispatchActions.createDriver, state => ({ ...state, loading: true })),
  on(dispatchActions.createDriverSuccess, state => ({ ...state, loading: false })),
  on(dispatchActions.createDriverFail, state => ({ ...state, loading: false })),

  on(dispatchActions.deleteDriver, state => ({ ...state, loading: true })),
  on(dispatchActions.deleteDriverSuccess, state => ({ ...state, loading: false })),
  on(dispatchActions.deleteDriverFail, state => ({ ...state, loading: false })),

  on(dispatchActions.searchLocations, state => ({ ...state, loading: true })),
  on(dispatchActions.searchLocationsSuccess, (state, payload) => ({
    ...state,
    loading: false,
    autocompleteResults: payload.autocompleteResults
  })),
  on(dispatchActions.searchLocationsFail, state => ({ ...state, loading: false })),

  on(dispatchActions.mapInitialized, (state, payload) => ({
    ...state,
    mapInitialized: true
  })),
  on(dispatchActions.setMapCenter, (state, payload) => ({
    ...state,
    mapCenter: { lat: payload.location.lat, lng: payload.location.lng }
  })),

  on(dispatchActions.setRidesTab, (state, payload) => ({ ...state, ridesTab: payload.tab, pageNumber: 0, pageSize: 20 })),

  on(dispatchActions.ignoreRide, (state, payload) => {
    const pinnedRides: Array<ProviderDispatchFeedItem> = cloneDeep(state.pinnedRides);
    const ignoredRideIndex = pinnedRides.findIndex(ride => ride.id === payload.ride.id);

    pinnedRides.splice(ignoredRideIndex, 1);
    return { ...state, pinnedRides: pinnedRides };
  }),
  on(dispatchActions.setIncludeIgnored, (state, payload) => ({ ...state, includeIgnored: payload.includeIgnored })),

  on(dispatchActions.listRides, state => ({ ...state, loading: true })),
  on(dispatchActions.listRidesSuccess, (state, payload) => {
    payload.rides.forEach((ride, index) => {
      if (ride.rideRequestType.onDemand) {
        payload.rides.splice(index, 1);
      }
    });
    return {
      ...state,
      loading: false,
      rides: payload.rides
    };
  }),

  on(dispatchActions.listRidesFail, state => ({ ...state, loading: false })),

  on(dispatchActions.filterRides, (state, payload) => ({
    ...state,
    filters: {
      upperLatitude: payload.filters['upperLatitude'] !== undefined ? payload.filters.upperLatitude : state.filters.upperLatitude,
      leftLongitude: payload.filters['leftLongitude'] !== undefined ? payload.filters.leftLongitude : state.filters.leftLongitude,
      lowerLatitude: payload.filters['lowerLatitude'] !== undefined ? payload.filters.lowerLatitude : state.filters.lowerLatitude,
      rightLongitude: payload.filters['rightLongitude'] !== undefined ? payload.filters.rightLongitude : state.filters.rightLongitude,
      beginDate: payload.filters['beginDate'] !== undefined ? payload.filters.beginDate : state.filters.beginDate,
      endDate: payload.filters['endDate'] !== undefined ? payload.filters.endDate : state.filters.endDate,
      beginTime: payload.filters['beginTime'] !== undefined ? payload.filters.beginTime : state.filters.beginTime,
      endTime: payload.filters['endTime'] !== undefined ? payload.filters.endTime : state.filters.endTime
    }
  })),

  on(dispatchActions.pageRides, (state, payload) => ({ ...state, pageNumber: payload.pageNumber, pageSize: payload.pageSize })),

  on(dispatchActions.pinRide, (state, payload) => {
    const pinnedRides = state.pinnedRides;
    if (isNullOrUndefined(pinnedRides.find(ride => ride.id === payload.ride.id))) {
      pinnedRides.push(payload.ride);
    }
    return { ...state, pinnedRides: pinnedRides };
  }),

  on(dispatchActions.unpinRide, (state, payload) => {
    const pinnedRides = state.pinnedRides;
    pinnedRides.splice(
      pinnedRides.findIndex(value => value.id === payload.ride.id),
      1
    );
    return { ...state, pinnedRides: pinnedRides };
  }),

  on(dispatchActions.deferHoverEvents, (state, payload) => ({ ...state, deferHoverEvents: payload.defer })),

  on(dispatchActions.assignDriverSuccess, (state, payload) => {
    const rides = cloneDeep(state.rides);
    const updatedRideIndex = rides.findIndex(ride => ride.id === payload.ride.id);

    rides.splice(updatedRideIndex, 1, payload.ride);

    return {
      ...state,
      rides: rides
    };
  }),

  // Itinerary Focus

  on(dispatchActions.focusItinerary, (state, payload) => {
    const itineraryRide: ProviderDispatchFeedItem = state.rides.find(ride => ride.itineraryId === payload.itineraryId);

    return {
      ...state,
      isItineraryFocused: true,
      focusedItineraryRiderName: isNullOrUndefined(itineraryRide) ? null : itineraryRide.riderName
    };
  }),

  on(dispatchActions.focusItinerarySuccess, (state, payload) => ({
    ...state,
    focusedItineraryRides: orderBy(payload.rides, ride => [get(ride, 'pickupDate', -1), get(ride, 'activatedAt')], ['asc', 'asc'])
  })),

  on(dispatchActions.focusItineraryFail, (state, payload) => ({
    ...state,
    isItineraryFocused: false,
    focusedItineraryRides: [],
    focusedItineraryRiderName: null
  })),

  on(dispatchActions.clearFocusedItinerary, (state, payload) => ({
    ...state,
    isItineraryFocused: false,
    focusedItineraryRides: [],
    focusedItineraryRiderName: null
  })),

  on(dispatchActions.listOnDemandRides, state => ({ ...state, loading: true })),
  on(dispatchActions.listOnDemandRidesSuccess, (state, payload) => {
    payload.onDemandRides.forEach((ride, index) => {
      if (!ride.rideRequestType.onDemand) {
        payload.onDemandRides.splice(index, 1);
      }
    });

    return {
      ...state,
      loading: false,
      onDemandRides: payload.onDemandRides
    };
  }),
  on(dispatchActions.listOnDemandRidesFail, state => ({ ...state, loading: false })),

  on(dispatchActions.pageOffPlatformReservations, (state, payload) => ({
    ...state,
    pageNumber: payload.pageNumber,
    pageSize: payload.pageSize
  })),

  on(dispatchActions.listOffPlatformReservationsSuccess, (state, payload) => ({
    ...state,
    offPlatformReservations: payload.reservations
  }))
);

export const reducer = (state: DispatchState, action: Action) => dispatchReducer(state, action);

export class ProviderFeeScheduleHelper {
  static getCurrentProviderFeeScheduleApprovalStatus(
    providerFeeScheduleApprovalStatuses: Array<ProviderFeeScheduleApprovalStatus>,
    providerId: number,
    transitProgramId: number,
    serviceLevelId: number
  ): ProviderFeeScheduleApprovalStatus {
    return providerFeeScheduleApprovalStatuses.find(providerFeeScheduleApprovalStatus => {
      return (
        providerFeeScheduleApprovalStatus.providerFeeSchedule.providerId == providerId &&
        providerFeeScheduleApprovalStatus.providerFeeSchedule.transitProgramId == transitProgramId &&
        providerFeeScheduleApprovalStatus.providerFeeSchedule.serviceLevelId == serviceLevelId &&
        providerFeeScheduleApprovalStatus.status === ApprovalObjectStatusCodeConstants.CURRENT
      );
    });
  }

  /**
   * Returns an object containing providerFeeScheduleEditableValues and providerFeeScheduleNonEditableValues that represent the form data.
   *
   * @param currentProviderFeeSchedule
   * @param selectedProviderFeeSchedule
   * @param hasModifiedFareCalculator
   */
  static getProviderFeeScheduleFormData(
    currentProviderFeeSchedule: ProviderFeeSchedule,
    selectedProviderFeeSchedule?: ProviderFeeSchedule,
    hasModifiedFareCalculator?: boolean
  ): { providerFeeScheduleEditableValues: ProviderFeeSchedule; providerFeeScheduleNonEditableValues: ProviderFeeSchedule } {
    const providerFeeScheduleEditableValues: ProviderFeeSchedule = new ProviderFeeSchedule();
    const providerFeeScheduleNonEditableValues: ProviderFeeSchedule = new ProviderFeeSchedule();

    // If only a current is passed in, clone the values from current to selected.
    if (isNullOrUndefined(selectedProviderFeeSchedule)) {
      selectedProviderFeeSchedule = cloneDeep(currentProviderFeeSchedule);
    }

    // If the fare calculator has been modified, then all values are new (no strike-through values).
    if (hasModifiedFareCalculator) {
      currentProviderFeeSchedule = cloneDeep(selectedProviderFeeSchedule);
    }

    Object.keys(currentProviderFeeSchedule).forEach(key => {
      const currentPropertyValue: any = currentProviderFeeSchedule[key];
      const selectedPropertyValue: any = selectedProviderFeeSchedule[key];

      if (currentPropertyValue === selectedPropertyValue) {
        providerFeeScheduleEditableValues[key] = currentPropertyValue;
        providerFeeScheduleNonEditableValues[key] = null;
      } else {
        providerFeeScheduleEditableValues[key] = selectedPropertyValue;
        providerFeeScheduleNonEditableValues[key] = currentPropertyValue;
      }
    });

    return {
      providerFeeScheduleEditableValues: providerFeeScheduleEditableValues,
      providerFeeScheduleNonEditableValues: providerFeeScheduleNonEditableValues
    };
  }

  static buildDefaultProviderFeeScheduleApprovalStatus(
    providerId: number,
    transitProgramId: number,
    serviceLevelId: number
  ): ProviderFeeScheduleApprovalStatus {
    const defaultProviderFeeSchedule: ProviderFeeSchedule = this.buildNewProviderFeeSchedule(providerId, transitProgramId, serviceLevelId);
    const defaultProviderFeeScheduleApprovalStatus: ProviderFeeScheduleApprovalStatus = new ProviderFeeScheduleApprovalStatus();
    defaultProviderFeeScheduleApprovalStatus.status = ApprovalObjectStatusCodeConstants.CURRENT;
    defaultProviderFeeScheduleApprovalStatus.object = Serialize(defaultProviderFeeSchedule);
    defaultProviderFeeScheduleApprovalStatus.reviewers = [];
    return defaultProviderFeeScheduleApprovalStatus;
  }

  static buildNewProviderFeeSchedule(providerId: number, transitProgramId: number, serviceLevelId: number): ProviderFeeSchedule {
    const providerFeeSchedule = new ProviderFeeSchedule();
    providerFeeSchedule.providerId = providerId;
    providerFeeSchedule.transitProgramId = transitProgramId;
    providerFeeSchedule.serviceLevelId = serviceLevelId;
    providerFeeSchedule.fareCalculatorId = 0;
    providerFeeSchedule.minimumFare = 0;
    providerFeeSchedule.baseFare = 0;
    providerFeeSchedule.distanceRate = 0;
    providerFeeSchedule.timeRate = 0;
    providerFeeSchedule.loadFee = 0;
    providerFeeSchedule.loadFeeBaseDistance = 0;
    providerFeeSchedule.mileageRate = 0;
    providerFeeSchedule.fuelSurchargeRate = 0;
    providerFeeSchedule.cancellationFee = 0;
    providerFeeSchedule.cancellationCredit = 0;
    providerFeeSchedule.noShowCredit = 0;
    return providerFeeSchedule;
  }
}
